import React, { Fragment, useRef } from 'react';

import useViewDynamicKendoReport from './UseViewDynamicKendoReport';

import Headings from '../headings/Headings';
import global from '../GlobalStyle.module.css';


import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import "@progress/kendo-theme-default/dist/all.css";
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { Button } from '@progress/kendo-react-buttons';

const ViewDynamicKendoReport = ({ reportId, reportParameters }) => {
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const mountedRef = React.useRef(true);

  const {
    reportData: hookReportData,
    reportTitle,
    isLoaded
  } = useViewDynamicKendoReport(reportId, reportParameters);

  // Cache reportData the first time values are returned
  const cachedReportDataRef = React.useRef(null);
  const reportDataSetRef = React.useRef(false);

  const reportData = React.useMemo(() => {
    // If reportData has been successfully cached once, always return the cached value
    if (reportDataSetRef.current && cachedReportDataRef.current !== null) {
      return cachedReportDataRef.current;
    }

    // If no data is available, return empty array
    if (!hookReportData || hookReportData.length === 0) {
      return [];
    }

    // Cache the reportData for the first time
    reportDataSetRef.current = true;
    cachedReportDataRef.current = hookReportData;

    return hookReportData;
  }, [hookReportData]);

  // Reset refreshing state when data is loaded (only if component is still mounted)
  React.useEffect(() => {
    if (isLoaded && mountedRef.current) {
      setIsRefreshing(false);
    }
  }, [isLoaded]);

  // Cleanup effect to mark component as unmounted
  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);


  const _export = useRef(null);
  const gridRef = useRef(null);

  // Track the title to prevent unnecessary regeneration
  const titleRef = React.useRef(null);
  const generatedTitleRef = React.useRef('');
  const titleSetRef = React.useRef(false);

  const getDynamicTitle = React.useMemo(() => {
    // If title has been successfully set once, always return the cached value
    if (titleSetRef.current && generatedTitleRef.current !== '') {
      return generatedTitleRef.current;
    }

    // If no title is provided, return empty string
    if (!reportTitle || reportTitle === '') {
      return '';
    }

    // Generate and cache the title for the first time
    const newTitle = reportTitle;
    
    // Mark as successfully set and cache the result
    titleSetRef.current = true;
    titleRef.current = reportTitle;
    generatedTitleRef.current = newTitle;

    return newTitle;
  }, [reportTitle]);

  // Check if reportData has a splits column
  const hasSplitsColumn = React.useMemo(() => {
    if (!reportData || reportData.length === 0) {
      return false;
    }
    return Object.keys(reportData[0]).some(key => key.toLowerCase() === 'splits');
  }, [reportData]);

  // Track the column structure to prevent unnecessary regeneration
  const columnsStructureRef = React.useRef(null);
  const generatedColumnsRef = React.useRef([]);
  const columnsSetRef = React.useRef(false);

  // Generate dynamic columns based on reportData structure
  const generateColumns = React.useMemo(() => {
    // If columns have been successfully set once, always return the cached value
    if (columnsSetRef.current && generatedColumnsRef.current.length > 0) {
      return generatedColumnsRef.current;
    }

    if (!reportData || reportData.length === 0) {
      return [];
    }

    // Get the keys from the first row to determine columns
    const firstRow = reportData[0];
    const columnKeys = Object.keys(firstRow).sort();
    const currentStructure = columnKeys.join(',');

    // Filter out the splits column if it exists (it will be shown in detail)
    const filteredKeys = columnKeys.filter(key => key.toLowerCase() !== 'splits');

    const newColumns = filteredKeys.map((key) => {
      // Create a title by converting camelCase or snake_case to proper case
      const title = key
        .replace(/([A-Z])/g, ' $1') // Add space before capital letters
        .replace(/_/g, ' ') // Replace underscores with spaces
        .replace(/^\w/, (c) => c.toUpperCase()) // Capitalize first letter
        .trim();

      // Determine column width based on data type and content
      const sampleValue = firstRow[key];
      let width = '120px';

      if (typeof sampleValue === 'string' && sampleValue.length > 20) {
        width = '200px';
      } else if (typeof sampleValue === 'object') {
        // If the value is an object, we can set a wider width
        width = '300px';
      } else if (typeof sampleValue === 'number') {
        width = '100px';
      } else if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time')) {
        width = '140px';
      } else if (key.toLowerCase().includes('memberid')) {
        width = '140px';
      } else if (key.toLowerCase().includes('id')) {
        width = '80px';
      }

      const columnConfig = {
        id: key,
        field: key,
        title: title,
        width: width
      };

      // Add date formatting if the field appears to be a date
      if (key.toLowerCase().includes('date') && typeof sampleValue === 'string') {
        columnConfig.format = '{0:dd/MM/yyyy}';
      } else if (typeof sampleValue === 'boolean') {
        columnConfig.cell = (props) => (
          <td>
            {props.dataItem[props.field] ? 'Yes' : 'No'}
          </td>
        );
      }

      return columnConfig;
    });

    // Mark as successfully set and cache the result
    columnsSetRef.current = true;
    columnsStructureRef.current = currentStructure;
    generatedColumnsRef.current = newColumns;

    return newColumns;
  }, [reportData]);

  // Track the splits column structure to prevent unnecessary regeneration
  const splitsStructureRef = React.useRef(null);
  const generatedSplitsColumnsRef = React.useRef([]);
  const splitsColumnsSetRef = React.useRef(false);

  // Generate columns for splits detail grid
  const generateSplitsColumns = React.useMemo(() => {
    // If splits columns have been successfully set once, always return the cached value
    if (splitsColumnsSetRef.current && generatedSplitsColumnsRef.current.length > 0) {
      return generatedSplitsColumnsRef.current;
    }

    if (!hasSplitsColumn || !reportData || reportData.length === 0) {
      return [];
    }

    // Find the first row that has splits data
    const rowWithSplits = reportData.find(row => {
      const splitsKey = Object.keys(row).find(key => key.toLowerCase() === 'splits');
      return splitsKey && row[splitsKey] && Array.isArray(row[splitsKey]) && row[splitsKey].length > 0;
    });

    if (!rowWithSplits) {
      return [];
    }

    const splitsKey = Object.keys(rowWithSplits).find(key => key.toLowerCase() === 'splits');
    const firstSplit = rowWithSplits[splitsKey][0];

    if (!firstSplit || typeof firstSplit !== 'object') {
      return [];
    }

    // Create structure signature for splits columns
    const splitsKeys = Object.keys(firstSplit).sort();
    const currentSplitsStructure = splitsKeys.join(',');

    const newSplitsColumns = splitsKeys.map((key) => {
      const title = key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/^\w/, (c) => c.toUpperCase())
        .trim();

      const sampleValue = firstSplit[key];
      let width = '120px';

      if (typeof sampleValue === 'string' && sampleValue.length > 20) {
        width = '200px';
      } else if (typeof sampleValue === 'number') {
        width = '100px';
      } else if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time')) {
        width = '140px';
      } else if (key.toLowerCase().includes('id')) {
        width = '80px';
      }

      const columnConfig = {
        id: key,
        field: key,
        title: title,
        width: width
      };

      // Add date formatting if the field appears to be a date
      if (key.toLowerCase().includes('date') && typeof sampleValue === 'string') {
        columnConfig.format = '{0:dd/MM/yyyy}';
      } else if (typeof sampleValue === 'boolean') {
        columnConfig.cell = (props) => (
          <td>
            {props.dataItem[props.field] ? 'Yes' : 'No'}
          </td>
        );
      }

      return columnConfig;
    });

    // Mark as successfully set and cache the result
    splitsColumnsSetRef.current = true;
    splitsStructureRef.current = currentSplitsStructure;
    generatedSplitsColumnsRef.current = newSplitsColumns;

    return newSplitsColumns;
  }, [reportData, hasSplitsColumn]);

  // Helper function to check if a row has splits data
  const hasRowSplitsData = (dataItem) => {
    const splitsKey = Object.keys(dataItem).find(key => key.toLowerCase() === 'splits');
    return !!(splitsKey && dataItem[splitsKey] && Array.isArray(dataItem[splitsKey]) && dataItem[splitsKey].length > 0);
  };

  // Detail component for splits
  const DetailComponent = (props) => {
    const dataItem = props.dataItem;
    // Find the splits key dynamically
    const splitsKey = Object.keys(dataItem).find(key => key.toLowerCase() === 'splits');

    if (!splitsKey || !dataItem[splitsKey] || !Array.isArray(dataItem[splitsKey])) {
      return <div style={{ padding: '10px' }}>No splits data available</div>;
    }

    const splitsData = dataItem[splitsKey];

    if (splitsData.length === 0) {
      return <div style={{ padding: '10px' }}>No splits data available</div>;
    }

    return (
      <div style={{ padding: '10px' }}>
        <h4>Splits Details</h4>
        <Grid
          data={splitsData}
          style={{ height: '300px' }}
          sortable={true}
        >
          {generateSplitsColumns.map((column) => (
            <Column
              key={column.field}
              field={column.field}
              title={column.title}
              width={column.width}
              format={column.format}
              cell={column.cell}
            />
          ))}
        </Grid>
      </div>
    );
  };

  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  const CustomCell = (props) => {
    return <td {...props.tdProps}>{props.dataItem.splits.length > 0 && props.children}</td>;
  };


  return (
    <div>
      <Fragment>
        <form noValidate>
          <div className="row">
            <div className="col-xs-12">
              <Headings.H3>{getDynamicTitle}</Headings.H3>
            </div>
          </div>
        </form>
      </Fragment>

      {!isLoaded || isRefreshing ? (
        <div className="k-loading-mask">
          <span className="k-loading-text">Loading</span>
          <div className="k-loading-image"></div>
          <div className="k-loading-color"></div>
        </div>
      ) : (
        <ExcelExport data={reportData} ref={_export}>
          <Grid
            style={{ height: '400px' }}
            data={reportData.map((item, index) => ({
              ...item,
              _gridId: index, // Add unique ID for grid functionality
              hasDetailRow: hasSplitsColumn && hasRowSplitsData(item)
            }))}
            dataItemKey="_gridId"
            ref={gridRef}
            autoProcessData={true}
            filterable={true}
            sortable={{
              mode: 'single'
            }}
            loading={!isLoaded || isRefreshing}
            detail={hasSplitsColumn ? DetailComponent : undefined}
            cells={{
              hierarchy: {
                data: CustomCell
              }
            }}
            onRowRender={(e) => {
              const originalItem = reportData.find(item =>
                JSON.stringify(item) === JSON.stringify({ ...e.dataItem, hasDetailRow: undefined, _gridId: undefined })
              );
              if (!hasRowSplitsData(originalItem)) {
                // Hide the expand button by removing the detail cell
                const detailCell = e.element.querySelector('.k-hierarchy-cell');
                if (detailCell) {
                  detailCell.style.display = 'none';
                }
              }
            }}
          >
            <GridToolbar>
              <Button title="Export Excel" themeColor={'primary'} type="button" onClick={excelExport}>
                Export to Excel
              </Button>
            </GridToolbar>
            {generateColumns.map((column) => (
              <Column
                key={column.field}
                field={column.field}
                title={column.title}
                width={column.width}
                format={column.format}
                cell={column.cell}
              />
            ))}
          </Grid>
        </ExcelExport>
      )}
    </div>
  );
}

export default ViewDynamicKendoReport;